import React from "react";

export const getFileExtension = (name) => {
  const m = (name || "").match(/\.([^.]+)$/);
  return m ? m[1].toLowerCase() : "";
};

export const EXT_GROUPS = {
  word: ["doc", "docx", "rtf", "odt"],
  excel: ["xls", "xlsx", "ods", "csv", "tsv"],
  pdf: ["pdf"],
  ppt: ["pptx", "ppt"],
  image: ["jpg", "jpeg", "png", "gif", "bmp", "webp", "avif", "svg", "heic", "heif"],
  video: ["mp4", "webm", "mov", "m4v", "mkv", "avi", "wmv"],
  audio: ["mp3", "wav", "ogg", "flac", "aac", "m4a"],
  archive: ["zip", "rar", "7z", "tar", "gz"],
  text: ["txt", "md", "log"],
};

export const classifyExt = (ext) => {
  const e = (ext || "").toLowerCase();
  if (EXT_GROUPS.word.includes(e)) return "word";
  if (EXT_GROUPS.excel.includes(e)) return "excel";
  if (EXT_GROUPS.pdf.includes(e)) return "pdf";
  if (EXT_GROUPS.ppt.includes(e)) return "ppt";
  if (EXT_GROUPS.image.includes(e)) return "image";
  if (EXT_GROUPS.video.includes(e)) return "video";
  if (EXT_GROUPS.audio.includes(e)) return "audio";
  if (EXT_GROUPS.archive.includes(e)) return "archive";
  if (EXT_GROUPS.text.includes(e)) return "text";
  return "other";
};

export const KIND_LABELS = {
  folder: "Folders",
  word: "Word",
  excel: "Excel",
  pdf: "PDF",
  ppt: "PowerPoint",
  image: "Pictures",
  video: "Videos",
  audio: "Audio",
  archive: "Archives",
  text: "Text",
  other: "Other",
};

export const KIND_ORDER = ["folder", "word", "excel", "pdf", "ppt", "image", "video", "audio", "archive", "text", "other"];

export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes === 0) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(size >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatDate = (timestamp, t, language) => {
  if (!timestamp) return "";
  const ms = typeof timestamp === "string"
    ? new Date(timestamp).getTime()
    : (timestamp < 1e12 ? timestamp * 1000 : timestamp);
  const date = new Date(ms);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t ? t('files.explorer.today', { defaultValue: 'Today' }) : 'Today';
  if (diffDays === 1) return t ? t('files.explorer.yesterday', { defaultValue: 'Yesterday' }) : 'Yesterday';
  if (diffDays < 7) {
    const key = diffDays === 1 ? 'files.explorer.days_ago_one' : 'files.explorer.days_ago';
    return t ? t(key, { count: diffDays, defaultValue: `${diffDays} days ago` }) : `${diffDays} days ago`;
  }
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    const key = weeks === 1 ? 'files.explorer.weeks_ago_one' : 'files.explorer.weeks_ago';
    return t ? t(key, { count: weeks, defaultValue: `${weeks} week${weeks > 1 ? "s" : ""} ago` }) : `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  }
  const monthName = new Intl.DateTimeFormat(language || 'en', { month: 'short' }).format(date);
  return `${monthName} ${date.getDate()}, ${date.getFullYear()}`;
};

export const getFileIcon = (item, size = 24) => {
  if (item.isDirectory) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M2 6C2 4.89543 2.89543 4 4 4H9L11 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z" fill="#f1c40f" />
        <path d="M2 8H22V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V8Z" fill="#f39c12" opacity="0.6" />
      </svg>
    );
  }

  const ext = getFileExtension(item.name);

  if (ext === "pdf") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#e74c3c" />
        <path d="M14 2V8H20" fill="#c0392b" />
        <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">PDF</text>
      </svg>
    );
  }

  if (EXT_GROUPS.image.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="2" fill="#27ae60" />
        <circle cx="8.5" cy="8.5" r="2" fill="#2ecc71" />
        <path d="M3 16L8 12L12 15L16 10L21 14V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V16Z" fill="#2ecc71" opacity="0.7" />
      </svg>
    );
  }

  if (EXT_GROUPS.word.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#3498db" />
        <path d="M14 2V8H20" fill="#2980b9" />
        <rect x="7" y="12" width="10" height="1.5" rx="0.75" fill="white" opacity="0.6" />
        <rect x="7" y="15" width="7" height="1.5" rx="0.75" fill="white" opacity="0.6" />
      </svg>
    );
  }

  if (EXT_GROUPS.ppt.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#e67e22" />
        <path d="M14 2V8H20" fill="#d35400" />
        <rect x="7" y="11" width="10" height="7" rx="1" fill="white" opacity="0.4" />
      </svg>
    );
  }

  if (EXT_GROUPS.excel.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#27ae60" />
        <path d="M14 2V8H20" fill="#1e8449" />
        <rect x="7" y="11" width="4" height="2" fill="white" opacity="0.5" />
        <rect x="12" y="11" width="4" height="2" fill="white" opacity="0.3" />
        <rect x="7" y="14" width="4" height="2" fill="white" opacity="0.3" />
        <rect x="12" y="14" width="4" height="2" fill="white" opacity="0.5" />
        <rect x="7" y="17" width="4" height="2" fill="white" opacity="0.5" />
        <rect x="12" y="17" width="4" height="2" fill="white" opacity="0.3" />
      </svg>
    );
  }

  if (EXT_GROUPS.archive.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#e67e22" />
        <path d="M14 2V8H20" fill="#d35400" />
        <rect x="10" y="4" width="3" height="2" fill="white" opacity="0.4" />
        <rect x="11" y="6" width="3" height="2" fill="white" opacity="0.3" />
        <rect x="10" y="8" width="3" height="2" fill="white" opacity="0.4" />
        <rect x="11" y="10" width="3" height="2" fill="white" opacity="0.3" />
        <rect x="10" y="13" width="4" height="3" rx="0.5" fill="white" opacity="0.5" />
      </svg>
    );
  }

  if (EXT_GROUPS.video.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="16" rx="2" fill="#9b59b6" />
        <path d="M10 8L16 12L10 16V8Z" fill="white" opacity="0.7" />
      </svg>
    );
  }

  if (EXT_GROUPS.audio.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="9" fill="#1abc9c" />
        <path d="M10 8L16 12L10 16V8Z" fill="white" opacity="0.7" />
      </svg>
    );
  }

  if (EXT_GROUPS.text.includes(ext)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#95a5a6" />
        <path d="M14 2V8H20" fill="#7f8c8d" />
        <rect x="7" y="11" width="10" height="1.2" rx="0.6" fill="white" opacity="0.5" />
        <rect x="7" y="13.5" width="8" height="1.2" rx="0.6" fill="white" opacity="0.4" />
        <rect x="7" y="16" width="10" height="1.2" rx="0.6" fill="white" opacity="0.5" />
        <rect x="7" y="18.5" width="5" height="1.2" rx="0.6" fill="white" opacity="0.4" />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" fill="#6c5ce7" />
      <path d="M14 2V8H20" fill="#5b4cdb" />
    </svg>
  );
};

export const getParentPath = (path) => {
  if (!path) return "";
  const idx = path.lastIndexOf("/");
  return idx > 0 ? path.substring(0, idx) : "";
};

export const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const getItemName = (item) => item?.name || item?.properties?.fileName || item?.label || "";
export const getItemKind = (item) => (item?.isDirectory ? "folder" : classifyExt(getFileExtension(getItemName(item))));

export const itemMatchesSearch = (item, q) => {
  if (!item) return false;
  const qq = norm(q || "");
  if (!qq) return true;
  return norm(getItemName(item)).includes(qq);
};

export const itemMatchesKind = (item, kind) => {
  if (kind === "all") return true;
  return getItemKind(item) === kind;
};
